import { useState, useEffect, useCallback } from "react";
import API from "@/lib/api";
import { toast } from "@/lib/notify";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  FileText, Download, Upload, FolderOpen, Search, Building2, Filter, X, Info, Eye, Plus
} from "lucide-react";
import { useSortableTable } from "@/hooks/useSortableTable";
import { SortableHead } from "@/components/SortableHead";

const CATEGORIES = ["Onboarding", "Declarations", "Notices", "Agreements", "Financial Reports", "Other"];

const CATEGORY_COLORS = {
  Onboarding: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  Declarations: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  Notices: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  Agreements: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  "Financial Reports": "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  Other: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

function formatFileSize(bytes) {
  if (!bytes) return "-";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1048576).toFixed(1) + " MB";
}

function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

export default function InvestorDocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterScope, setFilterScope] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [uploadDoc, setUploadDoc] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selfUploadDialog, setSelfUploadDialog] = useState(false);
  const [selfUploadFiles, setSelfUploadFiles] = useState([]);
  const [selfUploadName, setSelfUploadName] = useState("");
  const [selfUploadCategory, setSelfUploadCategory] = useState("Other");
  const [selfUploadProgress, setSelfUploadProgress] = useState(null);

  const fetchDocuments = useCallback(async () => {
    try {
      const params = {};
      if (filterCategory !== "all") params.category = filterCategory;
      if (filterScope !== "all") params.scope = filterScope;
      const res = await API.get("/documents", { params });
      setDocuments(res.data);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [filterCategory, filterScope]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDownload = async (doc) => {
    try {
      const res = await API.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || doc.name;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const handleInvestorUpload = async () => {
    if (!uploadFile) {
      toast.error("Please select a file");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadFile);
      await API.post(`/documents/${uploadDoc.id}/investor-upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded successfully");
      setUploadDoc(null);
      setUploadFile(null);
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleSelfUpload = async () => {
    if (!selfUploadFiles.length) {
      toast.error("Please select at least one file");
      return;
    }
    const isSingle = selfUploadFiles.length === 1;
    if (isSingle && !selfUploadName.trim()) {
      toast.error("Please provide a document name");
      return;
    }
    setUploading(true);
    let successCount = 0;
    const failures = [];
    try {
      for (let i = 0; i < selfUploadFiles.length; i++) {
        const file = selfUploadFiles[i];
        setSelfUploadProgress({ current: i + 1, total: selfUploadFiles.length, name: file.name });
        const docName = isSingle
          ? selfUploadName.trim()
          : file.name.replace(/\.[^/.]+$/, ""); // strip extension for multi-upload default
        const formData = new FormData();
        formData.append("file", file);
        const params = new URLSearchParams({ name: docName, category: selfUploadCategory });
        try {
          await API.post(`/documents/investor-self-upload?${params}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          successCount += 1;
        } catch (err) {
          failures.push({ name: file.name, reason: err.response?.data?.detail || "upload failed" });
        }
      }
      if (successCount > 0 && failures.length === 0) {
        toast.success(successCount === 1 ? "Document uploaded successfully" : `${successCount} documents uploaded successfully`);
      } else if (successCount > 0 && failures.length > 0) {
        toast.warning(`${successCount} uploaded, ${failures.length} failed`);
      } else {
        toast.error(`Upload failed: ${failures[0]?.reason || "unknown error"}`);
      }
      setSelfUploadDialog(false);
      setSelfUploadFiles([]);
      setSelfUploadName("");
      setSelfUploadCategory("Other");
      fetchDocuments();
    } finally {
      setSelfUploadProgress(null);
      setUploading(false);
    }
  };

  const handlePreview = async (doc) => {
    try {
      const res = await API.get(`/documents/${doc.id}/download?preview=1`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      window.open(url, "_blank");
    } catch {
      toast.error("Preview not available for this file type");
    }
  };

  const filteredDocs = documents.filter((d) => {
    // Trim leading/trailing whitespace so " somaiya "/"somaiya "/"somaiya"
    // all match the same rows.
    const q = (searchTerm || "").trim().toLowerCase();
    return !q || d.name.toLowerCase().includes(q) ||
      d.fund_name?.toLowerCase().includes(q);
  });

  const requestedDocs = filteredDocs.filter((d) => d.status === "requested");
  const activeDocs = filteredDocs.filter((d) => d.status === "active");

  const { sorted: sortedActiveDocs, sortKey, sortDir, toggleSort } = useSortableTable(activeDocs);

  return (
    <div className="animate-fade-in" data-testid="investor-documents-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="investor-documents-title">Documents</h1>
          <p className="text-muted-foreground text-sm mt-1">View and manage your documents</p>
        </div>
        <Button size="sm" className="gap-1.5" onClick={() => setSelfUploadDialog(true)} data-testid="investor-upload-doc-btn">
          <Plus className="w-3.5 h-3.5" /> Upload Document
        </Button>
      </div>

      {/* Pending Requests Banner */}
      {requestedDocs.length > 0 && (
        <Card className="mb-6 border-amber-500/30" data-testid="pending-requests-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/15 flex items-center justify-center">
                <Upload className="w-4 h-4 text-amber-400" />
              </div>
              <div>
                <p className="font-medium text-sm">Pending Upload Requests</p>
                <p className="text-xs text-muted-foreground">{requestedDocs.length} document{requestedDocs.length !== 1 ? "s" : ""} awaiting your upload</p>
              </div>
            </div>
            <div className="space-y-2">
              {requestedDocs.map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50" data-testid={`requested-doc-${doc.id}`}>
                  <div className="flex items-center gap-3">
                    <FileText className="w-4 h-4 text-amber-400" strokeWidth={1.5} />
                    <div>
                      <p className="text-sm font-medium">{doc.name}</p>
                      <p className="text-xs text-muted-foreground">{doc.fund_name} {doc.description ? `- ${doc.description}` : ""}</p>
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="gap-1.5 text-xs" onClick={() => { setUploadDoc(doc); setUploadFile(null); }} data-testid={`upload-requested-${doc.id}`}>
                    <Upload className="w-3.5 h-3.5" /> Upload
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
                data-testid="investor-search-documents"
              />
            </div>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px] h-9" data-testid="investor-filter-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterScope} onValueChange={setFilterScope}>
              <SelectTrigger className="w-[140px] h-9" data-testid="investor-filter-scope">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="fund">Fund Documents</SelectItem>
                <SelectItem value="investor">My Documents</SelectItem>
              </SelectContent>
            </Select>
            {(filterCategory !== "all" || filterScope !== "all" || searchTerm) && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterCategory("all"); setFilterScope("all"); setSearchTerm(""); }} className="h-9 gap-1 text-xs">
                <X className="w-3 h-3" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : activeDocs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FolderOpen className="w-12 h-12 text-muted-foreground/50 mb-3" strokeWidth={1} />
              <p className="text-muted-foreground text-sm">No documents available</p>
            </div>
          ) : (
            <Table data-testid="investor-documents-table">
              <TableHeader>
                <TableRow>
                  <SortableHead sortKey="name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="pl-6">Document</SortableHead>
                  <SortableHead sortKey="category" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Category</SortableHead>
                  <SortableHead sortKey="scope" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Type</SortableHead>
                  <SortableHead sortKey="fund_name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Fund</SortableHead>
                  <SortableHead sortKey="file_size" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Size</SortableHead>
                  <SortableHead sortKey="created_at" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Date</SortableHead>
                  <TableHead className="text-right pr-6">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedActiveDocs.map((doc) => (
                  <TableRow key={doc.id} data-testid={`investor-doc-row-${doc.id}`}>
                    <TableCell className="pl-6">
                      <div className="flex items-center gap-2.5">
                        <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                        <div>
                          <p className="font-medium text-sm">{doc.name}</p>
                          {doc.description && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{doc.description}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${CATEGORY_COLORS[doc.category] || CATEGORY_COLORS.Other}`}>
                        {doc.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        {doc.scope === "fund" ? <Building2 className="w-3 h-3" /> : <FileText className="w-3 h-3" />}
                        <span className="capitalize">{doc.scope === "fund" ? "Fund" : "Personal"}</span>
                      </div>
                    </TableCell>
                    <TableCell><span className="text-sm">{doc.fund_name || "-"}</span></TableCell>
                    <TableCell><span className="text-xs text-muted-foreground">{formatFileSize(doc.file_size)}</span></TableCell>
                    <TableCell><span className="text-xs text-muted-foreground">{formatDate(doc.created_at)}</span></TableCell>
                    <TableCell className="text-right pr-6">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handlePreview(doc)} data-testid={`investor-preview-${doc.id}`} title="Preview">
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(doc)} data-testid={`investor-download-${doc.id}`} title="Download">
                          <Download className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Investor Upload Dialog */}
      <Dialog open={!!uploadDoc} onOpenChange={(open) => { if (!open) { setUploadDoc(null); setUploadFile(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="investor-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Requested Document</DialogTitle>
            <DialogDescription>{uploadDoc?.name} - {uploadDoc?.description || "Upload the requested file"}</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-3">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-400">
                <p className="font-medium mb-1">Upload Guidelines</p>
                <ul className="space-y-0.5 text-blue-400/80">
                  <li>Accepted formats: PDF, DOCX, PNG, JPG</li>
                  <li>Ensure the document is clear and legible</li>
                  <li>Maximum file size: 50 MB</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="py-4">
            <Input type="file" onChange={(e) => setUploadFile(e.target.files[0])} data-testid="investor-upload-file-input" className="cursor-pointer" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadDoc(null); setUploadFile(null); }}>Cancel</Button>
            <Button onClick={handleInvestorUpload} disabled={uploading} data-testid="investor-upload-submit-btn">
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Self Upload Dialog */}
      <Dialog open={selfUploadDialog} onOpenChange={(open) => { if (!open) { setSelfUploadDialog(false); setSelfUploadFiles([]); setSelfUploadName(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="investor-self-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Documents</DialogTitle>
            <DialogDescription>Upload one or more documents to your investor portal</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Files</Label>
              <Input
                type="file"
                multiple
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  setSelfUploadFiles(files);
                  // If user didn't set a custom name and chose exactly one file, pre-fill name from filename
                  if (files.length === 1 && !selfUploadName.trim()) {
                    setSelfUploadName(files[0].name.replace(/\.[^/.]+$/, ""));
                  }
                }}
                data-testid="self-upload-file-input"
                className="cursor-pointer"
              />
              {selfUploadFiles.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="self-upload-selected-files">
                  {selfUploadFiles.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-xs bg-secondary/30 border border-border/50 rounded-md px-2 py-1.5">
                      <span className="truncate flex-1 mr-2">{f.name}</span>
                      <span className="text-muted-foreground flex-shrink-0">{formatFileSize(f.size)}</span>
                      <Button variant="ghost" size="icon" className="h-5 w-5 ml-1" onClick={() => {
                        setSelfUploadFiles(prev => prev.filter((_, idx) => idx !== i));
                      }}>
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                  {selfUploadFiles.length > 1 && (
                    <p className="text-[11px] text-muted-foreground mt-1">
                      {selfUploadFiles.length} files selected. Each will be saved using its filename as the document name.
                    </p>
                  )}
                </div>
              )}
            </div>
            {selfUploadFiles.length <= 1 && (
              <div className="space-y-1.5">
                <Label className="text-xs">Document Name</Label>
                <Input
                  placeholder="e.g., Tax Form, ID Document"
                  value={selfUploadName}
                  onChange={(e) => setSelfUploadName(e.target.value)}
                  data-testid="self-upload-name-input"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label className="text-xs">Category</Label>
              <Select value={selfUploadCategory} onValueChange={setSelfUploadCategory}>
                <SelectTrigger data-testid="self-upload-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              {selfUploadFiles.length > 1 && (
                <p className="text-[11px] text-muted-foreground">This category will be applied to all selected files.</p>
              )}
            </div>
            {selfUploadProgress && (
              <div className="text-xs text-muted-foreground border border-border/50 rounded-md px-3 py-2 bg-secondary/30" data-testid="self-upload-progress">
                Uploading {selfUploadProgress.current} of {selfUploadProgress.total}: <span className="text-foreground">{selfUploadProgress.name}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setSelfUploadDialog(false); setSelfUploadFiles([]); setSelfUploadName(""); }}>Cancel</Button>
            <Button
              onClick={handleSelfUpload}
              disabled={uploading || selfUploadFiles.length === 0 || (selfUploadFiles.length === 1 && !selfUploadName.trim())}
              data-testid="self-upload-submit-btn"
            >
              {uploading
                ? (selfUploadProgress ? `Uploading ${selfUploadProgress.current}/${selfUploadProgress.total}...` : "Uploading...")
                : selfUploadFiles.length > 1 ? `Upload ${selfUploadFiles.length} Files` : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
